/**
 * Prompts Page Tutorial
 * Guides users through browsing prompts, viewing reflections,
 * and using follow-up questions.
 * Uses demo category with pre-populated data so features are always visible.
 * Written for older, less tech-savvy users with simple language.
 *
 * Content tone:
 * - requiresClick steps use direct instructions ("Tap this to...")
 * - Informational steps use descriptive language ("You can use this to...")
 */

export const promptsPageTutorial = {
  id: 'prompts-page',
  name: 'How to Use Prompts',
  description: 'Learn how to browse prompts, view reflections, and use follow-up questions',

  // Show hidden Demo category during this tutorial
  demoCategory: true,

  // Enable followups so placeholder cards appear in tree view
  aiOverrides: {
    enable_followups: true
  },

  steps: [
    {
      id: 'navigation',
      selector: '.view-toggle',
      blockChildSelectors: ['.view-toggle button:not(:nth-child(2))'],
      title: 'Navigation',
      content: 'These buttons let you switch between the main pages: Home, Prompts, and Reflections. Tap Prompts to continue.',
      position: 'bottom',
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'categories',
      selector: '.categories',
      blockChildSelectors: ['.categories button:not(.active)'],
      title: 'Categories',
      content: 'Prompts are organized into categories. Each button shows a different group of questions. Tap the selected category to continue.',
      position: 'bottom',
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'prompt-cards',
      selector: '.prompt-card[data-has-reflections]',
      fallbackSelectors: ['.prompt-card'],
      blockChildSelectors: ['.reflection-btn-edit', '.reflection-btn-show'],
      title: 'Prompt Cards',
      content: 'Each card is a question to reflect on. They are organized by category. Tap one to expand it.',
      position: 'right',
      highlightPadding: 8,
      requiresClick: true
    },
    {
      id: 'add-button',
      selector: '.reflection-btn-edit',
      title: 'Add a Reflection',
      content: 'This button opens the Studio where you can record a new reflection for this prompt.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 3000,
      highlightPadding: 4,
      disableTargetClick: true
    },
    {
      id: 'view-button',
      selector: '.reflection-btn-show',
      title: 'View Reflections',
      content: 'Tap this button to see the reflections that have been recorded for this prompt.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 3000,
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'tree-toggle',
      selector: '.reflection-tree-toggle',
      title: 'Reflection Tree',
      content: 'Tap this toggle to open the tree view. It shows the conversation flow between reflections and follow-up questions.',
      position: 'right',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'tree-cards',
      selector: '.reflection-tree-container',
      title: 'Tree Cards',
      content: 'Each card in the tree is a reflection. You can click one to open it in the Studio and view or edit it.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 4,
      disableTargetClick: true
    },
    {
      id: 'followup-questions',
      selector: '.tree-mini-card.placeholder',
      title: 'Follow-up Questions',
      content: 'AI can suggest follow-up questions shown as cards with a "?" icon. You can hover over them to see the full question, then click to respond and reflect deeper.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 8,
      disableTargetClick: true
    },
    {
      id: 'new-badges',
      selector: '.new-badge',
      title: 'New Badges',
      content: 'When someone shares a reflection with you, a "NEW" badge appears so you know what you have not seen yet.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 3000,
      highlightPadding: 8,
      optional: true,
      disableTargetClick: true
    },
    {
      id: 'help-button',
      selector: '.help-btn',
      title: 'More Tutorials',
      content: 'Tap the "?" button any time to find more tutorials and learn about other features.',
      position: 'bottom',
      highlightPadding: 4
    },
    {
      id: 'complete',
      selector: null,
      title: 'Start Reflecting!',
      content: 'You now know how to browse prompts, view and add reflections, and use follow-up questions to reflect deeper. Give it a try!',
      position: 'center',
      isCompletionStep: true
    }
  ]
};
